// Dashboard shell: tab navigation across the trip list, per-trip report,
// aggregate view, diagnostics, and the live monitor. State is intentionally
// simple (no router dependency) - a single active-view + selected-trip pair.

import { useEffect, useState } from "react";
import { isPlaceholder, isSigned, listTrips } from "./api.js";
import type { TripSummary } from "./types.js";
import { TripList } from "./components/TripList.js";
import { TripReportView } from "./components/TripReportView.js";
import { Aggregate } from "./components/Aggregate.js";
import { Diagnostics } from "./components/Diagnostics.js";
import { LiveMonitor } from "./components/LiveMonitor.js";

type View = "trips" | "aggregate" | "diagnostics" | "live";

export function App() {
  const [view, setView] = useState<View>("trips");
  const [trips, setTrips] = useState<TripSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listTrips()
      .then((t) => {
        if (!alive) return;
        setTrips(t);
        if (t.length > 0) setSelectedId((prev) => prev ?? t[0].id);
      })
      .catch((e: unknown) => alive && setError(String(e)));
    return () => {
      alive = false;
    };
  }, []);

  const tab = (id: View, label: string) => (
    <button className={view === id ? "active" : ""} onClick={() => setView(id)}>
      {label}
    </button>
  );

  return (
    <div className="app">
      <header className="top">
        <h1>WakiDrive nav-telemetry</h1>
        {isPlaceholder ? (
          <span className="badge mock">mock data (no backend)</span>
        ) : (
          <span className={`badge ${isSigned ? "live" : ""}`}>
            {isSigned ? "signed" : "unsigned reads"}
          </span>
        )}
      </header>

      <nav className="tabs">
        {tab("trips", "Trips")}
        {tab("aggregate", "Aggregate")}
        {tab("diagnostics", "Diagnostics")}
        {tab("live", "Live")}
      </nav>

      {error && <p className="sev error">Failed to load trips: {error}</p>}

      {view === "trips" && (
        <div className="two-col">
          <div className="panel">
            <h3>Trips</h3>
            <TripList trips={trips} selectedId={selectedId} onSelect={setSelectedId} />
          </div>
          <div>
            {selectedId ? (
              <TripReportView tripId={selectedId} />
            ) : (
              <p className="muted">Select a trip.</p>
            )}
          </div>
        </div>
      )}

      {view === "aggregate" && <Aggregate trips={trips} />}
      {view === "diagnostics" && <Diagnostics />}
      {view === "live" && <LiveMonitor />}
    </div>
  );
}
